import React, { useContext, useEffect, useState, createContext } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { TableEditColumn } from '@devexpress/dx-react-grid-material-ui';
import { DataTypeProvider } from '@devexpress/dx-react-grid';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import { UploadFiles } from '../../../../components/Files';
import getAllAgentFilesQuery from '../../../../Apollo/queries/getAllAgentFiles';
import DataContext from '../../../../context/DataProvider/DataContext';
import Table from '../../../../components/Table';
import PdfPreview from '../../../../components/Files/pdfViewer/PdfPreview';
import deleteAgentFile from '../../../../Apollo/mutations/deleteAgentFile';
import TableSkeleton from '../../../../components/Table/TableSkeleton';
import Alert from '../../../../components/Alert';

type FileT = { id: number; filename: string; name: string; size: number; path: string };

const AgentFilesTableContext = createContext<any>(null);

function SizeFormatter({ value }: { value: number }) {
  return <span>{`${(value / (1024 * 1024)).toFixed(2)} MB`}</span>;
}

function FileSizeProvider() {
  return <DataTypeProvider formatterComponent={SizeFormatter} for={['size']} />;
}

function FileNameFormatter({ value, row }: { value: string; row?: any }) {
  const { setPdfPath, setPdfName, setOpen } = useContext(AgentFilesTableContext);
  return (
    <Tooltip title={value} placement="bottom-start">
      <span
        style={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit' }}
        aria-hidden="true"
        onClick={() => {
          setPdfPath(`${row.path}?file=${encodeURIComponent(row.filename)}`);
          setPdfName(row.name);
          setOpen(true);
        }}
      >
        {value}
      </span>
    </Tooltip>
  );
}

function FileNameProvider() {
  return <DataTypeProvider formatterComponent={FileNameFormatter} for={['name']} />;
}

const columns = [
  { name: 'name', title: 'Dateiname' },
  { name: 'size', title: 'Größe' },
];

function EditCellForCommand({ children, row: rowProp, ...props }: any) {
  const { setPdfPath, setPdfName, setOpen, loadingDelete, fileID } = useContext(AgentFilesTableContext);
  const row = rowProp as FileT;
  const isDeleting = loadingDelete && fileID === row.id;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <TableEditColumn.Cell row={rowProp} {...props}>
      {isDeleting ? (
        <CircularProgress />
      ) : (
        <>
          <IconButton
            onClick={() => {
              setPdfPath(`${row.path}?file=${encodeURIComponent(row.filename)}`);
              setPdfName(row.name);
              setOpen(true);
            }}
          >
            <FileOpenIcon fontSize="medium" sx={{ color: '#0000008a' }} />
          </IconButton>
          {children}
        </>
      )}
    </TableEditColumn.Cell>
  );
}

function AgentFilesTable() {
  const [open, setOpen] = useState(false);
  const [fileID, setFileID] = useState<number | null>(null);
  const [pdfPath, setPdfPath] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string | null>(null);
  const { agents } = useContext(DataContext);
  const [getAllAgentFiles, { data, refetch, loading, error: errorGetAllAgentFiles }] = useLazyQuery<{
    getAllAgentFiles: [FileT];
  }>(getAllAgentFilesQuery);

  const [deleteFile, { error: errorDeleteFile, loading: loadingDelete }] = useMutation(deleteAgentFile);

  const { number: AgentID } = useParams<{ number?: string }>();
  const agent = agents.find((a) => a.agent_id === parseInt(AgentID ?? '', 10));

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!data && agent && agent.id) {
      getAllAgentFiles({ variables: { agent_id: agent?.id } });
    }
  }, [data, agent?.id]);

  const contextValue = React.useMemo(
    () => ({ setPdfPath, setPdfName, setOpen, loadingDelete, fileID }),
    [setPdfPath, setPdfName, setOpen, loadingDelete, fileID],
  );

  if (errorGetAllAgentFiles || errorDeleteFile) {
    return <Alert />;
  }

  // Prevent race conditions: do not render the upload dropzone or table until we know the agent's internal backend ID.
  // The 'agents' list from DataContext can sometimes lag behind the initial route render.
  if (!agent?.id) {
    return <TableSkeleton />;
  }

  return (
    <>
      {loading && <TableSkeleton />}
      {!loading && (
        <>
          <PdfPreview open={open} handleClose={handleClose} pdfPath={pdfPath} filename={pdfName} />
          <UploadFiles agentId={agent?.id} refetch={refetch} />
          <AgentFilesTableContext.Provider value={contextValue}>
            <Table
              usageName="berater.dateien"
              defaultColumnWidths={[
                { columnName: 'name', width: 'auto' },
                { columnName: 'size', width: 120 },
              ]}
              EditCellForCommand={EditCellForCommand}
              onCommitChanges={({ deleted }) => {
                if (deleted && deleted[0]) {
                  setFileID(parseInt(deleted[0].toString(), 10));
                  deleteFile({ variables: { file_id: deleted[0], agent_id: agent?.id } })
                    .then(() => {
                      refetch();
                    })
                    .catch((e) => {
                      console.error('deleteFile:error', e);
                    });
                }
              }}
              row={data?.getAllAgentFiles ?? []}
              dataTypeProvider={[FileSizeProvider, FileNameProvider]}
              columns={columns}
              editable
              showDeleteCommand
            />
          </AgentFilesTableContext.Provider>
        </>
      )}
    </>
  );
}

export default AgentFilesTable;
